/**
 * Unified mapping from Claude SDK content block types to UI bubble types.
 * Single source of truth to eliminate duplication across streaming and historical paths.
 */

import type { Bubble } from "../streaming/reducer";

/**
 * Maps Claude SDK content block types to bubble types used in the UI.
 * This ensures consistency between live streaming and historical reconstruction.
 */
export const CONTENT_BLOCK_TO_BUBBLE_TYPE: Record<string, Bubble["type"]> = {
	text: "text",
	thinking: "thinking",
	reasoning: "reasoning",
	plan: "todo", // Note: plan blocks become todo bubbles in the UI
	tool_use: "tool_call",
	tool_result: "tool_result",
} as const;

/**
 * Get the bubble type for a given content block type.
 *
 * @param blockType - The SDK content block type (e.g., "text", "tool_use", "plan")
 * @returns The corresponding bubble type for UI rendering
 */
export function getContentBlockBubbleType(blockType: string): Bubble["type"] {
	return CONTENT_BLOCK_TO_BUBBLE_TYPE[blockType] ?? "text";
}

/**
 * Check if a content block type is recognized.
 *
 * @param blockType - The content block type to check
 * @returns True if the block type has a known mapping
 */
export function isKnownContentBlockType(blockType: string): boolean {
	return blockType in CONTENT_BLOCK_TO_BUBBLE_TYPE;
}

/**
 * Get all known content block types.
 *
 * @returns Array of valid content block type names
 */
export function getKnownContentBlockTypes(): string[] {
	return Object.keys(CONTENT_BLOCK_TO_BUBBLE_TYPE);
}
